'use client';

import { type ReactNode, useRef, useState } from 'react';

import Link from 'next/link';
import { usePathname } from 'next/navigation';

import { FlaskConical, LogIn, Menu, UserPlus } from 'lucide-react';

import {
  type CombinationDrug,
  DrugInteractionsPageShell,
  type InitialPanel,
  type InteractionCheckResult,
  type PaywallVariant,
} from '@kit/drug-interactions';
import type { Drug } from '@kit/drug-interactions/components/drug-search';
import {
  ResponsiveMenu,
  ResponsiveMenuContent,
  ResponsiveMenuItem,
  ResponsiveMenuTrigger,
} from '@kit/ui/responsive-menu';
import { Trans } from '@kit/ui/trans';

import { AppLogo } from '~/components/app-logo';
import { AuthButtons, buildAuthUrl } from '~/components/auth-buttons';
import { IncrediblesBar } from '~/components/incredibles-bar';
import { SyncBarFixed } from '~/components/sync-bar-fixed';
import pathsConfig from '~/config/paths.config';
import { getMaxDrugsForTier } from '~/config/subscription-tiers.config';
import { useSectionVisibility } from '~/lib/contexts/section-visibility-context';
import { useResetOnLogoClick } from '~/lib/hooks/use-reset-on-logo-click';
import {
  INCREDIBLES_BAR_HEIGHT,
  type IncrediblesBarVariant,
} from '~/lib/incredibles-bar';

import { HeroBranding } from './hero-branding';
import { HeroFeaturesLine } from './hero-features-line';

/**
 * Client wrapper for the drug interactions page shell, shared between the
 * homepage (/) and unauthenticated /baza-lekow/* routes.
 * Only rendered for unauthenticated users — do not reuse on authenticated
 * routes without adding auth state handling.
 */
interface DrugInteractionsPageContentProps {
  initialDrugs?: Drug[];
  /** Content rendered above the footer (e.g. features carousel) */
  aboveFooter?: ReactNode;
  /** Auto-open a document panel on mount (from URL state) */
  initialPanel?: InitialPanel;
  /** Server-rendered interaction results */
  initialInteractionResults?: InteractionCheckResult[];
  /** Access mode for server-rendered interaction results */
  initialInteractionAccess?: 'none' | 'full' | 'paywalled';
  /** Paywall variant for server-rendered interaction results */
  initialInteractionPaywallVariant?: PaywallVariant | null;
  /** Server-rendered top combinations for SEO */
  initialTopCombinations?: CombinationDrug[];
  /** Show drugs without ChPL extraction */
  showUnavailableDrugs?: boolean;
  /** Maximum drugs that can be selected (tier-dependent) */
  maxDrugs?: number;
  /** Called when the document panel opens or closes */
  onPanelChange?: (
    panel: { type: 'chpl' | 'ulotka'; drugId: string } | null,
  ) => void;
  /** InCredibles bar variant resolved on the server (null = hidden) */
  incrediblesBar?: IncrediblesBarVariant | null;
}

export function DrugInteractionsPageContent({
  initialDrugs,
  aboveFooter,
  initialPanel,
  initialInteractionResults,
  initialInteractionAccess,
  initialInteractionPaywallVariant,
  initialTopCombinations,
  showUnavailableDrugs,
  maxDrugs,
  onPanelChange,
  incrediblesBar = null,
}: DrugInteractionsPageContentProps) {
  const pathname = usePathname();
  // Anonymous visitors: /pricing is gated by the `main_*` scope, not `nav_*`
  const { main_pricing } = useSectionVisibility();
  const resetRef = useRef<(() => void) | null>(null);
  const handleLogoClick = useResetOnLogoClick(resetRef);
  const [incrediblesDismissed, setIncrediblesDismissed] = useState(false);
  const showIncrediblesBar = incrediblesBar !== null && !incrediblesDismissed;

  const header = (
    <div className="flex w-full flex-1 items-center justify-between">
      <AppLogo showName onClick={handleLogoClick} />
      <div className="ml-auto flex items-center">
        {/* Desktop: show auth buttons directly */}
        <div className="hidden items-center gap-2 sm:flex">
          <AuthButtons />
        </div>
        {/* Mobile: hamburger menu */}
        <div className="flex items-center gap-2 sm:hidden">
          <ResponsiveMenu>
            <ResponsiveMenuTrigger
              aria-label="Open menu"
              className="focus:outline-primary flex cursor-pointer items-center rounded-md p-1.5 transition-colors hover:bg-zinc-100 dark:hover:bg-zinc-800"
            >
              <Menu className="h-5 w-5 text-zinc-600 dark:text-zinc-400" />
            </ResponsiveMenuTrigger>
            <ResponsiveMenuContent align="end" className="min-w-[180px]">
              <ResponsiveMenuItem asChild icon={LogIn}>
                <Link href={buildAuthUrl(pathsConfig.auth.signIn, pathname)}>
                  <Trans i18nKey="auth:signIn" />
                </Link>
              </ResponsiveMenuItem>
              <ResponsiveMenuItem asChild icon={UserPlus}>
                <Link href={buildAuthUrl(pathsConfig.auth.signUp, pathname)}>
                  <Trans i18nKey="auth:signUp" />
                </Link>
              </ResponsiveMenuItem>
            </ResponsiveMenuContent>
          </ResponsiveMenu>
        </div>
      </div>
    </div>
  );

  return (
    <DrugInteractionsPageShell
      showPricing={main_pricing}
      isAuthenticated={false}
      isAuthLoading={false}
      initialDrugs={initialDrugs}
      enableUrlSync
      header={header}
      topBar={
        showIncrediblesBar ? (
          <IncrediblesBar
            variant={incrediblesBar}
            onDismiss={() => setIncrediblesDismissed(true)}
          />
        ) : undefined
      }
      topBarHeight={showIncrediblesBar ? INCREDIBLES_BAR_HEIGHT : 0}
      branding={(ctx) => <HeroBranding compact={ctx.hasDrugs} />}
      beforeFooter={aboveFooter}
      searchInputClassName={({ hasDrugs }) =>
        hasDrugs ? 'rounded-xl' : 'sm:h-16 rounded-xl'
      }
      searchPlaceholder="Wyszukaj lek po nazwie, substancji czynnej..."
      emptyStatePaddingTop="pt-[12vh]"
      searchFooterOverride={({ handleLoadExample }) => (
        <HeroFeaturesLine onLoadExample={() => void handleLoadExample()} />
      )}
      searchInputEndAddon={({ handleLoadExample }) => (
        <button
          type="button"
          onClick={() => void handleLoadExample()}
          className="cursor-pointer rounded-lg bg-stone-100 px-2.5 py-2 text-xs font-medium whitespace-nowrap text-green-600 transition-colors hover:bg-stone-200 hover:text-green-700 dark:bg-stone-800/50 dark:text-green-400 dark:hover:bg-stone-700/50 dark:hover:text-green-300"
        >
          <FlaskConical className="-mt-0.5 inline h-3.5 w-3.5" /> Zobacz
          przykładową analizę
        </button>
      )}
      footer={({ isViewerOpen, panelWidth }) => (
        <>
          <div className="mt-auto pb-14" />
          <SyncBarFixed isViewerOpen={isViewerOpen} panelWidth={panelWidth} />
        </>
      )}
      initialPanel={initialPanel}
      initialInteractionResults={initialInteractionResults}
      initialInteractionAccess={initialInteractionAccess}
      initialInteractionPaywallVariant={initialInteractionPaywallVariant}
      initialTopCombinations={initialTopCombinations}
      showUnavailableDrugs={showUnavailableDrugs}
      maxDrugs={maxDrugs ?? getMaxDrugsForTier(null)}
      onPanelChange={onPanelChange}
      onResetRef={(reset) => {
        resetRef.current = reset;
      }}
    />
  );
}
